type Observers = typeof IntersectionObserver | typeof MutationObserver
type ExtractCallback<O> = O extends typeof IntersectionObserver
  ? IntersectionObserverCallback
  : MutationCallback
type ExtractInit<O> = O extends typeof IntersectionObserver
  ? IntersectionObserverInit
  : MutationObserverInit
type ExtractType<O> = O extends typeof IntersectionObserver
  ? IntersectionObserver
  : MutationObserver

class Observer<T extends Observers> {
  private instances: { [initKey: string]: ExtractType<T> } = {}
  private callbacks: { [initKey: string]: Array<ExtractCallback<T>> } = {}
  constructor(private readonly observerClass: T) {}

  onChange(
    cb: ExtractCallback<T>,
    init: ExtractInit<T>,
    target?: Element
  ): ExtractType<T> {
    const initKey = JSON.stringify(init)
    const instance = (this.instances[initKey] =
      this.instances[initKey] ||
      // @ts-ignore
      new this.observerClass(this.fireCallbacks(initKey), init))
    this.callbacks[initKey] = this.callbacks[initKey] || []
    this.callbacks[initKey].push(cb)

    if (target) {
      this.observe(target, init)
    }

    return instance
  }

  observe(target: Element, init: ExtractInit<T>): ExtractType<T> {
    const initKey = JSON.stringify(init)
    const observer = (this.instances[initKey] =
      this.instances[initKey] ||
      // @ts-ignore
      new this.observerClass(this.fireCallbacks(initKey), init))
    // @ts-ignore
    observer.observe(target, init)

    return observer
  }

  disconnect(target: Element, init: ExtractInit<T>): ExtractType<T> {
    const initKey = JSON.stringify(init)
    const observer = (this.instances[initKey] =
      this.instances[initKey] ||
      // @ts-ignore
      new this.observerClass(this.fireCallbacks(initKey), init))
    // @ts-ignore
    observer.disconnect(target, init)

    return observer
  }

  private fireCallbacks = (initKey: string) => (entries, observer) => {
    this.callbacks[initKey].forEach((cb) => cb(entries, observer))
  }
}

let mutationInstance: Observer<typeof MutationObserver>
export const getMutationObserver = () =>
  (mutationInstance = mutationInstance || new Observer(MutationObserver))
let intersectionInstance: Observer<typeof IntersectionObserver>
export const getIntersectionObserver = () =>
  (intersectionInstance =
    intersectionInstance || new Observer(IntersectionObserver))
